import type { MergedOfferItemData } from '@/interfaces/offer';
import type { SearchFormDataType } from '@/interfaces/search-form';
import { deepMerge } from '@/js/utils/utils';
import { getDefaultCurrency } from '@utils/environmentUtils';
import { offerItemDecor } from '@/components/common/services/transform';
import type { MaybeRef } from 'vue';
import { unref } from 'vue';
import axios from 'axios';
import type { AvailabilityItemData, FlightSegmentItem, PackageAvailabilityItem } from '@/interfaces/availability';
import { getTravelType } from '@/js/utils/offerUtils';
import request from './request';
import { hasValue } from '@/js/utils/objectUtils';

const endpoint = '/v1/verify-offer';

function createPayload(item: MergedOfferItemData, formData: SearchFormDataType) {
	const { adult, children } = formData.travelers;
	return JSON.parse(
		JSON.stringify({
			TravelType: getTravelType(item.type),
			OfferID: item.OfferID,
			Adults: adult,
			CurrencyCode: getDefaultCurrency(),
			...(children.length ? { Children: children } : {}),
		})
	);
}

function handleMissingAirports(response: AvailabilityItemData) {
	const offer = response.Offer;
	if (!offer) return;
	if ('OutboundFlightSegments' in offer && offer.OutboundFlightSegments.length) {
		offer.OutboundFlightSegments[0].Departure.Airport = '';
		offer.OutboundFlightSegments[0].Departure.AirportCode = '';
		offer.OutboundFlightSegments[offer.OutboundFlightSegments.length - 1].Arrival.Airport = '';
		offer.OutboundFlightSegments[offer.OutboundFlightSegments.length - 1].Arrival.AirportCode = '';
	}
	if ('InboundFlightSegments' in offer && offer.InboundFlightSegments.length) {
		offer.InboundFlightSegments[0].Departure.Airport = '';
		offer.InboundFlightSegments[0].Departure.AirportCode = '';
		offer.InboundFlightSegments[offer.InboundFlightSegments.length - 1].Arrival.Airport = '';
		offer.InboundFlightSegments[offer.InboundFlightSegments.length - 1].Arrival.AirportCode = '';
	}
}

export async function verifyOffer(
	isDebuggingAirportsMissing: MaybeRef<boolean>,
	formData: MaybeRef<SearchFormDataType>,
	item: MergedOfferItemData,
	cancelTokenName: string
) {
	const {
		Price: unverifiedPrice,
		CurrencyCode: unverifiedCurrency,
		StartDate: unverifiedStartDate,
		BoardType: unverifiedBoardType,
	} = item;
	const unverifiedEndDate = item?.InboundFlight?.Departure?.Date || item.EndDate;
	const unverifiedRoomCode = item.Room ? item.Room.Code : '';
	const payload = createPayload(item, unref(formData));

	let update: MergedOfferItemData = {
		...item,
		available: false,
		availableChecked: true,
		Status: undefined,
	};
	try {
		const resp = await request<AvailabilityItemData>({ url: endpoint, data: payload, method: 'post' }, cancelTokenName);
		if (resp.Available) {
			const { OutboundFlightSegments, InboundFlightSegments } = resp.Offer as PackageAvailabilityItem;
			const hasMissingValues = [OutboundFlightSegments, InboundFlightSegments]?.some((segments) =>
				segments?.some((segment) => {
					if ((['FlightNumber', 'Airline'] as (keyof FlightSegmentItem)[]).some((key) => !hasValue(segment[key]))) {
						return true;
					}
					if ([segment.Arrival, segment.Departure].some((entry) => [...Object.values(entry)].some((value) => !hasValue(value)))) {
						return true;
					}
					return false;
				})
			);
			if (hasMissingValues) {
				console.error('[LOG - FLIGHTSEGMENT nullish value]', { 'Flight Segment Response': JSON.stringify(resp) });
			}
			// TODO: Remove debugging mode for missing airports
			if (unref(isDebuggingAirportsMissing)) {
				handleMissingAirports(resp);
			}

			update = {
				OfferFeatures: {},
				...deepMerge(item, offerItemDecor(resp.Offer, item.type)),
				available: true,
				availableChecked: true,
				SessionID: resp.SessionID,
				Status: resp.Status,
				unverifiedPrice,
				unverifiedCurrency,
				unverifiedStartDate,
				unverifiedEndDate,
				unverifiedBoardType,
				unverifiedRoomCode,
			} as MergedOfferItemData;
			if (Object.hasOwn(resp.Offer, 'Flight')) {
				const { Flight } = resp.Offer as PackageAvailabilityItem & {
					Flight: { Inbound: { Duration: number }; Outbound: { Duration: number } };
				};

				//Overwrites unverified Flight Duration with verified Duration, uses old Duration as fallback
				const inboundFlight = { ...update.InboundFlight };
				inboundFlight.Duration = Flight.Inbound.Duration ?? inboundFlight.Duration;
				const outboundFlight = { ...update.OutboundFlight };
				outboundFlight.Duration = Flight.Outbound.Duration ?? outboundFlight.Duration;

				update = { ...update, InboundFlight: inboundFlight, OutboundFlight: outboundFlight } as MergedOfferItemData;
			}
		} else {
			update.Status = resp.Status;
		}
	} catch (e) {
		if (!axios.isCancel(e)) {
			console.error(e);
		}
	}
	return update;
}
